// Level loader: validates a level file with zod and turns its map into MapData
// (offset [col,row] in the file → axial (q,r) in the game). docs/05-CAMPAIGN.md → Level file format.
import { compareHex, hexKey, isAdjacent, offsetToAxial } from '../sim/hex';
import type { Cover, HexCell, MapData } from '../sim/types';
import { HEX_TOKEN, levelSchema, type LevelFile, type LevelFileMap } from './schemas';

const COVER: Record<string, Cover> = {
  o: 'open',
  w: 'woods',
  u: 'urban',
  s: 'swamp',
  '~': 'water',
};

export type LoadedLevel = {
  id: string;
  title: string;
  map: MapData;
  /** The validated file, for parts later milestones read (brief, units, orders). */
  file: LevelFile;
};

export class LevelError extends Error {}

/** Validate and convert a level's map. Throws LevelError with a readable message. */
export function parseMap(m: LevelFileMap, where = 'map'): MapData {
  const { width, height } = m;
  if (m.rows.length !== height) {
    throw new LevelError(`${where}: expected ${height} rows, found ${m.rows.length}`);
  }
  const cells: HexCell[] = [];
  m.rows.forEach((row, r) => {
    const tokens = row.split(' ');
    if (tokens.length !== width) {
      throw new LevelError(`${where}: row ${r} has ${tokens.length} hexes, expected ${width}`);
    }
    tokens.forEach((t, c) => {
      if (!HEX_TOKEN.test(t)) {
        throw new LevelError(`${where}: bad hex "${t}" at [${c},${r}] (use o/w/u/s/~ + 0-9)`);
      }
      cells.push({ cover: COVER[t[0] as string] as Cover, elev: Number(t[1]) });
    });
  });

  const inside = (o: readonly [number, number], label: string): void => {
    const [c, r] = o;
    if (c < 0 || r < 0 || c >= width || r >= height) {
      throw new LevelError(`${where}: ${label} [${c},${r}] is off the ${width}×${height} map`);
    }
  };

  // + 0 turns a JSON -0 into 0, so coordinates are canonical.
  const axial = (o: readonly [number, number]) => offsetToAxial([o[0] + 0, o[1] + 0]);
  const kindsByPair = new Map<string, Set<string>>();
  const edges = m.edges.map((e, i) => {
    inside(e.a, `edge ${i} a`);
    inside(e.b, `edge ${i} b`);
    const p = axial(e.a);
    const q = axial(e.b);
    if (!isAdjacent(p, q)) {
      throw new LevelError(
        `${where}: edge ${i} joins [${e.a.join(',')}] and [${e.b.join(',')}], which are not neighbors`,
      );
    }
    // Canonical order (a before b) so lookups never depend on how the file wrote it.
    const [a, b] = compareHex(p, q) <= 0 ? [p, q] : [q, p];
    const pair = `${hexKey(a)}|${hexKey(b)}`;
    const kinds = kindsByPair.get(pair) ?? new Set<string>();
    if (kinds.has(e.kind)) {
      throw new LevelError(`${where}: edge ${i} repeats a ${e.kind} that is already listed`);
    }
    kinds.add(e.kind);
    kindsByPair.set(pair, kinds);
    const water = kinds.has('river') || kinds.has('bridge');
    if (
      (kinds.has('river') && (kinds.has('road') || kinds.has('bridge'))) ||
      (water && kinds.has('stream'))
    ) {
      throw new LevelError(
        `${where}: edge ${i} mixes ${[...kinds].sort().join(' + ')}; a road over a river is one "bridge" edge`,
      );
    }
    return { a, b, kind: e.kind };
  });

  const obstacles = m.obstacles.map((o, i) => {
    inside(o.hex, `obstacle ${i}`);
    return { hex: axial(o.hex), kind: o.kind };
  });

  return { width, height, cells, edges, obstacles };
}

/** Parse a level file (already JSON-decoded). */
export function parseLevel(json: unknown): LoadedLevel {
  const res = levelSchema.safeParse(json);
  if (!res.success) {
    const first = res.error.issues[0];
    const path = first ? first.path.join('.') : '';
    throw new LevelError(`level file invalid at ${path || '(root)'}: ${first?.message ?? ''}`);
  }
  const file = res.data;
  return { id: file.id, title: file.title, map: parseMap(file.map, file.id), file };
}

// Campaign levels are bundled as separate chunks, loaded on demand.
const campaign = import.meta.glob<{ default: unknown }>('./levels/*.json');

/** Ids of the bundled campaign levels, sorted. */
export function levelIds(): string[] {
  return Object.keys(campaign)
    .map((p) => p.replace('./levels/', '').replace('.json', ''))
    .sort();
}

export async function loadLevel(id: string): Promise<LoadedLevel> {
  const load = campaign[`./levels/${id}.json`];
  if (!load) throw new LevelError(`unknown level "${id}"`);
  return parseLevel((await load()).default);
}
